import styled from "styled-components";
import {useRef, useState} from "react";
import {useAppStore} from "../../../../../StateManager";
import {DomPortal} from "../../../../modules/abstract/portal/DomPortal";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance, fontSize, fontWeight, radius, VerticalFlexStyle, zIndex} from "../../../styles/styles";
import {PopupBackdropV5} from "../../../popup/PopupBackdropV5";
import CloseIcon from "../../../icons/xmark-large.svg?react";
import CirclePlusIcon from "../../../icons/circle-plus.svg?react";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import {Functions} from "../../../../../utils/function/Functions";
import {ViewPreferenceCategory, XyiconFeature} from "../../../../../generated/api/base";
import {TextInputV5, TextInputWrapperStyled} from "../../../details/datatypes/TextInputV5";
import {CheckboxInputStyled, CheckboxInputV5} from "../../../details/datatypes/CheckboxInputV5";
import {ButtonV5} from "../../../button/ButtonV5";
import {SelectInputStyled, SelectInputV5} from "../../../input/select/SelectInputV5";
import {InfoButtonStyled} from "../../../button/InfoButtonV5";
import {View} from "../../../../../data/models/View";
import {Report} from "../../../../../data/models/Report";
import type {IViewFolder} from "../../../../../data/models/ViewUtils";
import {
	createGlobalView,
	getFoldersFromViewFolderStructure,
	getItemByIdInViewFolderStructure,
	rootFolderId,
} from "../../../../../data/models/ViewUtils";
import type {WorkspaceViewType} from "../../../navigation/views/WorkspaceViewCommon";
import type {ViewActionType} from "../../../sharing/GlobalSharingPopup";
import {GlobalSharingPopup} from "../../../sharing/GlobalSharingPopup";
import {onWorkspaceViewClick} from "../../../topbar/ViewTabsCommon";
import type {IModuleOption} from "../../../modules/report/create/wizard/pages/details/ReportDetailsV5";
import {useCloseOnKeyboardPress} from "../../../utils";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import ClipboardTextIcon from "../../../../5.0/icons/clipboard-text.svg?react";
import {ViewTemplateCard} from "./ViewTemplateCard";
import {getTypeFromFeatureForCard} from "./CardUtils";

const getPlaceholderForFeature = (feature: XyiconFeature): string => {
	switch (feature) {
		case XyiconFeature.Xyicon:
			return "Xyicon List View Name";
		case XyiconFeature.Boundary:
			return "Boundary List View Name";
		case XyiconFeature.SpaceEditor:
			return "Space View Name";
		case XyiconFeature.Report:
			return "Report View Name";
	}
};

const arrayOfTemplates: XyiconFeature[] = [XyiconFeature.Xyicon, XyiconFeature.Boundary, XyiconFeature.SpaceEditor, XyiconFeature.Report];

const favoriteRootFolder: IViewFolder = {
	name: "Favorites",
	id: rootFolderId,
	isOpen: true,
	category: ViewPreferenceCategory.Folder,
	children: [],
};

interface ICreateViewPopupProps {
	readonly type: WorkspaceViewType;
	readonly viewFolderIdToSaveNewViewInto: string;
	readonly onClose: () => void;
	readonly defaultTemplateFeature?: XyiconFeature;
	readonly onCreateCallback?: (view: View) => void;
	readonly onCreateReportView?: (report: Report, selectedFavoriteFolder: string, viewFolderIdToSaveNewViewInto: string) => void;
}

export const CreateViewPopup = (props: ICreateViewPopupProps) => {
	const moduleOptions: IModuleOption[] = [
		{
			id: XyiconFeature.Xyicon,
			label: "Xyicon",
		},
		{
			id: XyiconFeature.Boundary,
			label: "Boundary",
		},
	];

	const {onClose, onCreateCallback, onCreateReportView, type, viewFolderIdToSaveNewViewInto, defaultTemplateFeature = XyiconFeature.Xyicon} = props;
	const isGlobal = type === "Global";
	const appState = useAppStore((state) => state.appState);
	const [selectedTemplateFeature, setSelectedTemplateFeature] = useState<XyiconFeature>(defaultTemplateFeature);
	const [newViewName, setNewViewName] = useState<string>("");
	const [newViewDescription, setNewViewDescription] = useState<string>("");
	const [addToFavorites, setAddToFavorites] = useState<boolean>(type === "Favorites");
	const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);
	const [isGlobalActionPanelOpen, setIsGlobalActionPanelOpen] = useState<boolean>(false);
	const [globalActionType, setGlobalActionType] = useState<ViewActionType>("modify");
	const [selectedModule, setSelectedModule] = useState<IModuleOption>(moduleOptions.find((module) => module.label === "Xyicon") ?? null);

	const favoriteViews = appState.user?.favoriteViews || [];
	favoriteRootFolder.children = favoriteViews;
	const folders: IViewFolder[] = [favoriteRootFolder, ...getFoldersFromViewFolderStructure(favoriteViews)];
	const [selectedFavoriteFolder, setSelectedFavoriteFolder] = useState<IViewFolder>(
		(viewFolderIdToSaveNewViewInto ? (getItemByIdInViewFolderStructure(favoriteViews, viewFolderIdToSaveNewViewInto) as IViewFolder) : folders[0]) ??
			folders[0],
	);
	const descriptionRef = useRef<HTMLTextAreaElement>(null);
	const descriptionMaxHeight = 90;

	const onDescriptionInput = (value: string) => {
		setNewViewDescription(value);

		if (descriptionRef.current.scrollHeight < descriptionMaxHeight) {
			descriptionRef.current.style.minHeight = `${descriptionRef.current.scrollHeight}px`;
		}
	};

	useCloseOnKeyboardPress(onClose, KeyboardListener.KEY_ESCAPE);

	const createView = async () => {
		const {organization, app, actions} = appState;

		if (selectedTemplateFeature === XyiconFeature.Report) {
			const report = Report.createNew(appState, XyiconFeature.Report);
			report.name = newViewName;
			report.reportFeature = selectedModule.id;
			report.description = newViewDescription;
			report.isGlobal = isGlobal;

			onCreateReportView(report, addToFavorites ? selectedFavoriteFolder.id : null, viewFolderIdToSaveNewViewInto);
			onClose();
		} else {
			const newDefaultView = View.createNew(selectedTemplateFeature, newViewName, appState);
			const newDefaultViewData = newDefaultView.getData();

			const {result: viewData} = await app.transport.services.view.create(
				{
					feature: selectedTemplateFeature,
					name: newViewName,
					columns: newDefaultViewData.columns,
					isGlobal,
					additionalDetails: JSON.stringify({description: newViewDescription}),
				},
				selectedTemplateFeature,
				isGlobal && !!viewFolderIdToSaveNewViewInto,
			);

			if (viewData) {
				const newView = actions.getViewById(viewData.viewID);

				if (newView) {
					actions.selectViewById(newView.id);

					if (addToFavorites) {
						await newView.setFavorite(true, selectedFavoriteFolder.id);
					}

					if (isGlobal && organization && viewFolderIdToSaveNewViewInto) {
						await createGlobalView(newView.id, ViewPreferenceCategory.View, appState, viewFolderIdToSaveNewViewInto);
					}

					onCreateCallback?.(newView);
					onClose();
					onWorkspaceViewClick(newView, false);
				}
				setIsWaitingForResponse(false);
			}
		}
	};

	const onCreateClick = () => {
		setIsWaitingForResponse(true);

		if (props.type === "Global" && selectedTemplateFeature !== XyiconFeature.Report) {
			setIsGlobalActionPanelOpen(true);
			setGlobalActionType("create");
		} else {
			createView();
		}
	};

	const onCreateCancelClick = () => {
		setIsGlobalActionPanelOpen(false);
		setIsWaitingForResponse(false);
	};

	const onGlobalActionClose = (saveChanges: boolean) => {
		if (!saveChanges) {
			onCreateCancelClick();
		}
	};

	const getErrorMessage = () => {
		return !isNameValid ? (newViewName === "" ? "Name cannot be empty!" : "Name needs to be unique!") : "";
	};

	const isNameValid = appState.actions.isNameValidForViewOrReport(
		newViewName,
		selectedTemplateFeature,
		"",
		selectedTemplateFeature === XyiconFeature.Report,
	);

	if (isGlobalActionPanelOpen) {
		return (
			<GlobalSharingPopup
				onClose={onGlobalActionClose}
				viewActionType={globalActionType}
				isReports={false}
				onConfirm={createView}
			/>
		);
	}

	return (
		<DomPortal destination={appState.app.modalContainer}>
			<PopupBackdropV5 onClick={Functions.stopPropagation}>
				<CreateViewPopupStyled>
					<HeaderStyled>
						<TitleStyled>Create a New {isGlobal ? "global " : ""}View</TitleStyled>
						<IconButtonV5
							IconComponent={CloseIcon}
							onClick={onClose}
						/>
					</HeaderStyled>
					<MainStyled>
						<TemplateContainerStyled>
							<H3Styled>Templates</H3Styled>
							<GridStyled>
								{arrayOfTemplates
									.filter((template) => !(template === XyiconFeature.Report && !appState.user.isAdmin))
									.map((f) => (
										<ViewTemplateCard
											key={f}
											feature={f}
											isActive={selectedTemplateFeature === f}
											onClick={() => setSelectedTemplateFeature(f)}
										/>
									))}
							</GridStyled>
						</TemplateContainerStyled>
						<SelectedTemplateCardContainerStyled>
							<H3Styled>{getTypeFromFeatureForCard(selectedTemplateFeature)}</H3Styled>
							<ViewNameContainerStyled>
								<NameLabelStyled>Name</NameLabelStyled>
								<TextInputV5
									placeholder={getPlaceholderForFeature(selectedTemplateFeature)}
									value={newViewName}
									onInput={setNewViewName}
									getErrorMessage={getErrorMessage}
								/>
							</ViewNameContainerStyled>
							{selectedTemplateFeature === XyiconFeature.Report && (
								<ViewNameContainerStyled>
									<NameLabelStyled>Module</NameLabelStyled>
									<SelectInputV5
										options={moduleOptions}
										renderOptionWhenClosed={(option) => option.label}
										selected={selectedModule}
										onChange={(module) => setSelectedModule(module)}
										isFixedWidth={true}
										dropdownFixedWidth="256px"
										optionsZIndex={999999}
									/>
								</ViewNameContainerStyled>
							)}
							<LowerPartStyled>
								<MidContainerStyled>
									<ViewTemplateCard
										isInFocus={true}
										feature={selectedTemplateFeature}
										onClick={Functions.emptyFunction}
										isActive={false}
									/>
									<DescriptionStyled>
										<ClipboardTextIcon />
										<TextInputV5
											isTextArea={true}
											value={newViewDescription}
											onInput={onDescriptionInput}
											divRef={descriptionRef}
											placeholder="Add Description"
										/>
									</DescriptionStyled>
									<AddToFavoritesContainerStyled>
										<CheckboxInputV5
											label="Add to Favorites"
											value={addToFavorites}
											onChange={setAddToFavorites}
										/>
									</AddToFavoritesContainerStyled>
									{addToFavorites && (
										<SelectContainerStyled>
											<SelectInputV5
												selected={selectedFavoriteFolder}
												onChange={setSelectedFavoriteFolder}
												options={folders}
												optionsZIndex={zIndex.popup + 1}
												isSameWidth={true}
												renderOptionWhenClosed={(viewFolder: IViewFolder) => viewFolder.name}
											/>
										</SelectContainerStyled>
									)}
								</MidContainerStyled>
								<BottomPartStyled>
									<ButtonV5
										label={isWaitingForResponse ? "CREATING..." : "CREATE"}
										onClick={onCreateClick}
										disabled={!isNameValid || isWaitingForResponse}
									>
										<CirclePlusIcon />
									</ButtonV5>
								</BottomPartStyled>
							</LowerPartStyled>
						</SelectedTemplateCardContainerStyled>
					</MainStyled>
				</CreateViewPopupStyled>
			</PopupBackdropV5>
		</DomPortal>
	);
};

const DescriptionStyled = styled.div`
	${VerticalFlexStyle};
	flex: 1;
	align-self: stretch;
	position: relative;

	svg {
		z-index: 9999;
		position: absolute;
		top: ${baseDistance.sm};
		left: ${baseDistance.sm};
	}

	textarea {
		flex: 1;
		padding-left: ${baseDistance.lg};
		border-radius: ${radius.sm};
		border-color: ${colorPalette.gray.c300};
		font-size: ${fontSize.md};
		padding: 7px ${baseDistance.sm} 0 ${baseDistance.lg};
		max-height: ${baseDistance.lg};

		&:focus {
			border-color: ${colorPalette.primary.c500Primary};
		}
	}
`;

const SelectContainerStyled = styled.div`
	display: flex;
	justify-content: center;

	${SelectInputStyled} {
		min-width: 180px;
		margin-left: 120px;
	}
`;

const LowerPartStyled = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex: 1;
`;

const BottomPartStyled = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const MidContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.md};
`;

const AddToFavoritesContainerStyled = styled.div`
	display: flex;

	label {
		color: ${colorPalette.gray.c700Dark};
	}

	${CheckboxInputStyled} {
		display: flex;
		gap: 48px;
		color: ${colorPalette.gray.c950};
	}
`;

const NameLabelStyled = styled.div`
	font-size: ${fontSize.lg};
	line-height: 24px;
	color: ${colorPalette.gray.c950};
	font-weight: ${fontWeight.normal};
`;

const ViewNameContainerStyled = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: relative;
	gap: ${baseDistance.md};

	${InfoButtonStyled} {
		position: absolute;
		left: initial;
		right: 0;
		top: 7px;
	}

	${TextInputWrapperStyled}, ${SelectInputStyled} {
		width: 256px;
	}
`;

const SelectedTemplateCardContainerStyled = styled.div`
	flex: 1;
	padding: ${baseDistance.md} ${baseDistance.lg};
	display: flex;
	flex-direction: column;
	max-width: 456px;
	gap: ${baseDistance.md};
`;

const GridStyled = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${baseDistance.md};
`;

const H3Styled = styled.h3`
	font-size: ${fontSize.xl};
	line-height: 24px;
	font-weight: ${fontWeight.bold};
	text-transform: capitalize;
`;

const TemplateContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.md};
	border-right: 1px solid ${colorPalette.gray.c200Light};
	padding: ${baseDistance.md} ${baseDistance.lg};
	width: 624px;
`;

const MainStyled = styled.div`
	display: flex;
`;

const TitleStyled = styled.div`
	font-size: 24px;
	line-height: 32px;
	font-weight: ${fontWeight.bold};
	color: ${colorPalette.gray.c950};
`;

const HeaderStyled = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 96px;
	padding: ${baseDistance.lg};
	border-bottom: 1px solid ${colorPalette.gray.c200Light};
`;

const CreateViewPopupStyled = styled.div`
	display: flex;
	flex-direction: column;
	width: 1080px;
	background-color: ${colorPalette.white};
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
	border-radius: ${radius.xxl};
	padding-bottom: ${baseDistance.md};
`;
